import type {
  SubscriptionGroupType,
  SubscriptionPlan,
} from "@/types/subscription";
import type { SubscriptionPlanDto } from "@ckweb/ckfetch/connectables/setup";
import type { OrganizationSubscriptionDto } from "@ckweb/ckfetch/connectables/organization";

export const expirationPeriodDays: Record<SubscriptionGroupType, number> = {
  none: 0,
  day: 1,
  week: 7,
  month: 30,
  year: 365,
} as const;

export const allExpirationTypes: SubscriptionGroupType[] = [
  "day",
  "week",
  "month",
  "year",
];

// TODO: how can we make this dynamic, is there a need for that?
const extendedPlans: Record<string, Partial<SubscriptionPlan>> = {
  EarlyAdopter: {
    features: [
      { value: "cksetup.subscription.planEarlyAdopterFeature1" },
      { value: "cksetup.subscription.planEarlyAdopterFeature2" },
    ],
    name: "cksetup.subscription.planEarlyAdopter",
    description: "cksetup.subscription.planEarlyAdopterDescription",
  },
  Basic: {
    facilityCapKey: "cksetup.subscription.facilityCapFeature",
    features: [
      { value: "cksetup.subscription.planBasicFeature1" },
      { value: "cksetup.subscription.planBasicFeature2" },
    ],
    name: "cksetup.subscription.planBasic",
    description: "cksetup.subscription.planBasicDescription",
  },
  Starter: {
    facilityCapKey: "cksetup.subscription.facilityCapFeature",
    features: [
      { value: "cksetup.subscription.planStarterFeature1" },
      { value: "cksetup.subscription.planStarterFeature2" },
    ],
    name: "cksetup.subscription.planStarter",
    description: "cksetup.subscription.planStarterDescription",
  },
  Power: {
    features: [
      { value: "cksetup.subscription.planPowerFeature1" },
      { value: "cksetup.subscription.planPowerFeature2" },
    ],
    name: "cksetup.subscription.planPower",
    description: "cksetup.subscription.planPowerDescription",
  },
  Premium: {
    features: [
      { value: "cksetup.subscription.planPremiumFeature1" },
      { value: "cksetup.subscription.planPremiumFeature2" },
    ],
    name: "cksetup.subscription.planPremium",
    description: "cksetup.subscription.planPremiumDescription",
  },
};

export function enrichAvailablePlans(
  availablePlans: SubscriptionPlanDto[],
  currentSubscription?: OrganizationSubscriptionDto,
): SubscriptionPlan[] {
  const currentPlan = currentSubscription?.isActive
    ? availablePlans.find((plan) => plan.slug === currentSubscription?.slug)
    : undefined;
  const result = availablePlans.map((plan) => {
    const extended = extendedPlans[plan.slug] ?? {
      // TODO: while we think of a dynamic way to add new plans we will default to basic features
      ...extendedPlans.Basic,
      name: plan.prices[0]?.name || plan.slug,
    };
    const features = [
      {
        value: extended.facilityCapKey ?? "cksetup.subscription.perFacility",
        args: {
          count: plan.facilityCap,
        },
      },
      {
        value: "cksetup.subscription.equipmentAreaCapFeature",
        args: {
          count: plan.equipAreaCap,
        },
      },
      ...(extended.features ?? []),
    ];

    const isDisabled = plan.sortOrder <= (currentPlan?.sortOrder ?? 0);
    const prices = plan.prices.map((price) => ({
      ...price,
      isCurrent:
        currentSubscription?.isActive &&
        currentSubscription?.subscriptionId === price.id,
      isDisabled: isDisabled || !price.isActive,
    }));

    const priceGroups = new Map(
      allExpirationTypes.map((type) => [
        type,
        {
          prices: prices.filter((price) => price.expirationType === type),
        },
      ]),
    );

    return {
      ...plan,
      ...extended,
      features,
      isCurrent:
        currentSubscription?.isActive &&
        currentSubscription?.slug === plan.slug,
      isDisabled,
      prices,
      priceGroups,
    } as SubscriptionPlan;
  });

  return result.sort((a, b) => a.sortOrder - b.sortOrder);
}

export const fixedSubscriptionSteps = [
  {
    id: "select-plan",
    url: "select-plan",
    hideSidebar: true,
  },
  {
    id: "signup",
    url: "signup",
    title: "auth.createAccount",
  },
  {
    id: "payment",
    title: "cksetup.subscription.payment-step.title",
    url: "payment",
    disableForFree: true,
  },
] as const;

export type SubscriptionStepId = (typeof fixedSubscriptionSteps)[number]["id"];
export type SubscriptionStep = WizardStep & {
  id: SubscriptionStepId;
  index: number;
  optional?: boolean;
  disableForFree?: boolean;
};

export const subscriptionSteps = fixedSubscriptionSteps.map((step, index) => ({
  ...step,
  index,
})) as SubscriptionStep[];

export function isSubscriptionStepDisabled(
  step: SubscriptionStep,
  {
    lastVisitedStepIndex,
    isFree,
  }: {
    lastVisitedStepIndex: number;
    isFree?: boolean;
  },
) {
  if (step.disabled && !step.hideSidebar && step.index > lastVisitedStepIndex) {
    return true;
  }

  if (isFree && step.disableForFree) {
    return true;
  }

  return false;
}
